<script>
import { defineComponent } from "vue";
import UsersMixin from "@/views/users/mixins/UsersMixin.vue";
import BaseView from "@/mixins/BaseView.vue";
import SearchField from "@/components/inputs/SearchField.vue";
import UserSelect from "@/views/users/components/UserSelect.vue";
import TableFilter from "@/components/tables/TableFilter.vue";
import TableFilterRow from "@/components/tables/TableFilterRow.vue";
import OmniSelect from "@/components/selects/OmniSelect.vue";
import { MasksMixin } from "@/mixins/masks.mixin.ts";
import StateField from "@/components/inputs/StateField.vue";
import ZipField from "@/components/inputs/ZipField.vue";
import DatePicker from "@/components/pickers/datePicker/DatePicker.vue";
import PhoneField from "@/components/inputs/PhoneField.vue";

export default defineComponent({
  name: "OwnersSearchForm",
  components: {
    PhoneField,
    DatePicker,
    ZipField,
    StateField,
    OmniSelect,
    TableFilterRow,
    TableFilter,
    SearchField,
    UserSelect,
  },
  mixins: [BaseView, UsersMixin, MasksMixin],
  emits: ["search"],
  data() {
    return {
      params: {},
      showAdditionalFilters: false,
    };
  },
  created() {
    this.params = this.getSearchParams("owners");
    this.search();
  },
  methods: {
    clear() {
      this.params = {};
      this.search();
    },
    search() {
      this.setSearchParams("owners", this.params);
      this.$emit("search", this.params);
    },
  },
});
</script>

<template>
  <table-filter
    :filters="params"
    :header-filter-keys="['name', 'status', 'truck', 'phone', 'email']"
    @apply="search"
    @clear="clear"
  >
    <template #header>
      <table-filter-row :divider="false" :label="$t('general.filter-main')" icon="mdi-filter-variant">
        <v-col>
          <search-field id="search--name-field" v-model="params.name" :label="$t('general.name')" @search="search" />
        </v-col>
        <v-col>
          <omni-select
            id="search--status-select"
            v-model="params.status"
            :clearable="true"
            :items="ownerStatuses"
            :label="$t('general.status')"
            color="primary"
            density="compact"
            hide-details
            item-title="name"
            item-value="id"
            variant="outlined"
            @update:model-value="search"
          />
        </v-col>
        <v-col>
          <search-field id="search--truck-field" v-model="params.truck" :label="$t('drivers.truck')" @search="search" />
        </v-col>
        <v-col>
          <phone-field v-model="params.phone" @search="search" />
        </v-col>
        <v-col>
          <search-field
            id="search--email-field"
            v-model="params.email"
            :label="$t('general.email')"
            type="email"
            @search="search"
          />
        </v-col>
      </table-filter-row>
    </template>
    <table-filter-row :label="$t('general.address')" icon="mdi-map-marker-radius-outline">
      <v-col>
        <search-field id="search--city-field" v-model="params.city" :label="$t('general.city')" @search="search" />
      </v-col>
      <v-col>
        <state-field v-model="params.state" @search="search" />
      </v-col>
      <v-col>
        <zip-field v-model="params.zip" @search="search" />
      </v-col>
    </table-filter-row>
    <table-filter-row :label="$t('general.workers')" icon="mdi-account-multiple-outline">
      <v-col cols="4">
        <user-select
          id="search--driver-select"
          v-model="params.driver"
          :label="$t('users.driver')"
          drivers
          @change="search"
        />
      </v-col>
      <v-col cols="4">
        <user-select
          id="search--coordinator-select"
          v-model="params.coordinator"
          :label="$t('users.coordinator')"
          coordinators
          @change="search"
        />
      </v-col>
    </table-filter-row>
    <table-filter-row :label="$t('general.company-info')" icon="mdi-badge-account-horizontal-outline">
      <v-col cols="4">
        <date-picker
          id="search--insurance-expire-date-from"
          v-model="params.insuranceExpireFrom"
          :label="$t('general.insurance-exp-date-from')"
          @search="search"
        />
      </v-col>

      <v-col cols="4">
        <date-picker
          id="search--insurance-expire-date-to"
          v-model="params.insuranceExpireTo"
          :label="$t('general.insurance-exp-date-to')"
          @search="search"
        />
      </v-col>
    </table-filter-row>
  </table-filter>
</template>
